// src/services/authService.js
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  sendEmailVerification,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { userService } from './userService';
import toast from 'react-hot-toast';

export const authService = {
  // Email/Password Authentication
  async signUpWithEmail(email, password, displayName) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update user profile with display name
      await updateProfile(result.user, {
        displayName: displayName
      });

      // Create user document in Firestore
      await userService.createUserProfile(result.user, { displayName });

      // Send email verification
      await sendEmailVerification(result.user);
      
      toast.success('Account created successfully! Please check your email for verification.');
      return result.user;
    } catch (error) {
      console.error('Error signing up:', error);
      throw this.getAuthError(error);
    }
  },

  async signInWithEmail(email, password) {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Update last login time
      await userService.updateUserProfile(result.user.uid, {
        lastLoginAt: new Date()
      });

      toast.success('Welcome back!');
      return result.user;
    } catch (error) {
      console.error('Error signing in:', error);
      throw this.getAuthError(error);
    }
  },

  // Google Authentication
  async signInWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      
      // Create or update user profile
      await userService.createUserProfile(result.user, {
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        provider: 'google'
      });

      toast.success(`Welcome, ${result.user.displayName}!`);
      return result.user;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw this.getAuthError(error);
    }
  },

  // Sign Out
  async signOut() {
    try {
      await signOut(auth);
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      throw this.getAuthError(error);
    }
  },

  // Password Reset
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw this.getAuthError(error);
    }
  },

  // Update Password
  async updateUserPassword(newPassword) {
    try {
      await updatePassword(auth.currentUser, newPassword);
      toast.success('Password updated successfully');
    } catch (error) {
      console.error('Error updating password:', error);
      throw this.getAuthError(error);
    }
  },

  // Update Profile
  async updateUserProfile(displayName, photoURL) {
    try {
      await updateProfile(auth.currentUser, {
        displayName: displayName || auth.currentUser.displayName,
        photoURL: photoURL || auth.currentUser.photoURL
      });

      // Update Firestore profile
      await userService.updateUserProfile(auth.currentUser.uid, {
        displayName: displayName || auth.currentUser.displayName,
        photoURL: photoURL || auth.currentUser.photoURL,
        updatedAt: new Date()
      });

      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      throw this.getAuthError(error);
    }
  },

  // Resend Email Verification
  async resendEmailVerification() {
    try {
      await sendEmailVerification(auth.currentUser);
      toast.success('Verification email sent!');
    } catch (error) {
      console.error('Error sending verification email:', error);
      throw this.getAuthError(error);
    }
  },

  // Auth State Observer
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  },

  // Get Current User
  getCurrentUser() {
    return auth.currentUser;
  },

  // Error handling
  getAuthError(error) {
    const errorMessages = {
      'auth/user-not-found': 'No account found with this email address.',
      'auth/wrong-password': 'Incorrect password.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/invalid-email': 'Invalid email address.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/network-request-failed': 'Network error. Please check your connection.',
      'auth/requires-recent-login': 'This operation requires recent authentication. Please sign in again.',
      'auth/popup-closed-by-user': 'Sign-in was cancelled.',
      'auth/cancelled-popup-request': 'Sign-in was cancelled.',
    };

    return new Error(errorMessages[error.code] || error.message || 'An authentication error occurred.');
  }
};